<script lang="ts">
  import { onMount } from 'svelte';
  import { useLocation } from 'svelte-routing';
  import { _ } from 'svelte-i18n';
  import { api } from '../lib/api';
  import type {
    ReportType,
    ReportEntryDto,
    ReportQueryResponse,
    ReportFilterField,
  } from '../lib/api';
  import { activeHouseholdStore } from '../lib/stores/activeHouseholdStore';
  import { householdsStore } from '../lib/stores/householdsStore';
  import type { FilterValue } from '../lib/components/ui/FilterToolbar.svelte';
  import PageHeader from '../lib/components/layout/PageHeader.svelte';
  import ReportTable from '../lib/components/reports/ReportTable.svelte';
  import HandwerkerReportTable from '../lib/components/reports/HandwerkerReportTable.svelte';

  // svelte-routing v2 does NOT pass `location` as a prop to routed components.
  // Subscribe to the router location store so query-only changes (e.g. filter
  // navigation within the same report) are observed after mount ([T-016] / #211).
  const location = useLocation();
  let mounted = false;

  const defaultHousehold = householdsStore.defaultHousehold;
  $: members = $defaultHousehold?.members ?? [];

  export let reportType = '';

  let reportMeta: ReportType | null = null;
  let response: ReportQueryResponse | null = null;
  let entries: ReportEntryDto[] = [];
  let loading = true;
  let error = '';

  // State from URL — defaults resolved from plugin meta after load
  let sortBy = 'entry_date';
  let sortOrder: 'asc' | 'desc' = 'desc';
  let pageSize = 20;
  let page = 0;
  let year: string | null = null;
  let category: string | null = null;
  let person: string | null = null;
  let minAmount: number | null = null;
  let maxAmount: number | null = null;
  let deductible: string | null = null;

  // Tracks which filter field names have a schema-defined DefaultValue.
  // When a user explicitly clears such a field back to "all" (null), we write
  // an empty URL param (e.g. ?deductible=) so that on reload the URL presence
  // signals "user explicitly chose no filter" vs. "param was never set".
  let fieldsWithDefault: Set<string> = new Set();

  $: filterState = {
    year,
    category,
    person,
    minAmount,
    maxAmount,
    deductible,
  };

  function parseUrlState() {
    const params = new URLSearchParams(window.location.search);
    // Explicit URL params take precedence; plugin meta default applied after metadata load.
    sortBy = params.get('sortBy') || 'entry_date';
    sortOrder = (params.get('sortOrder') as 'asc' | 'desc') || 'desc';
    pageSize = parseInt(params.get('pageSize') || '20', 10);
    page = parseInt(params.get('page') || '0', 10);
    year = params.get('year');
    category = params.get('category');
    person = params.get('person');
    deductible = params.get('deductible');
    const minParam = params.get('minAmount');
    const maxParam = params.get('maxAmount');
    minAmount = minParam ? parseFloat(minParam) : null;
    maxAmount = maxParam ? parseFloat(maxParam) : null;
  }

  // React to URL query changes after mount (same pattern as Browse.svelte [T-016]/#239).
  // Guard on the location search string (prevLocationSearch), NOT just `mounted`:
  // without it, the mounted=true flip (and any reactive invalidation) re-parses the
  // stale, default-less URL via parseUrlState() and wipes the schema default applied
  // in onMount (e.g. deductible=yes), leaving a flaky filtered-but-no-chip state (#257).
  // Only act when the URL's query string itself actually changed (back/forward, in-app nav).
  let prevLocationSearch: string | null = null;
  $: if (mounted && $location && $location.search !== prevLocationSearch) {
    prevLocationSearch = $location.search;
    parseUrlState();
    // Re-apply schema defaults for fields whose param is absent, so a fresh navigation
    // (no query) re-applies the default consistently with onMount.
    if (reportMeta?.fields) {
      applySchemaDefaults(reportMeta.fields, new URLSearchParams($location.search));
    }
    if (reportMeta) {
      loadEntries();
    }
  }

  function updateUrlState() {
    const params = new URLSearchParams();
    if (sortBy !== 'entry_date') params.set('sortBy', sortBy);
    if (sortOrder !== 'desc') params.set('sortOrder', sortOrder);
    if (pageSize !== 20) params.set('pageSize', pageSize.toString());
    if (page > 0) params.set('page', page.toString());
    if (year) params.set('year', year);
    else if (fieldsWithDefault.has('year')) params.set('year', '');
    if (category) params.set('category', category);
    else if (fieldsWithDefault.has('category')) params.set('category', '');
    if (person) params.set('person', person);
    else if (fieldsWithDefault.has('person')) params.set('person', '');
    if (deductible) params.set('deductible', deductible);
    else if (fieldsWithDefault.has('deductible')) params.set('deductible', '');
    if (minAmount != null) params.set('minAmount', minAmount.toString());
    if (maxAmount != null) params.set('maxAmount', maxAmount.toString());

    const qs = params.toString();
    const newUrl = `/reports/${reportType}${qs ? '?' + qs : ''}`;
    window.history.replaceState({}, '', newUrl);
  }

  /**
   * Apply schema-defined DefaultValue for each filter field, but only when the
   * corresponding URL parameter is absent. An explicit URL param (including an
   * empty value like `?deductible=`) always wins — this ensures that:
   *  - A fresh navigation picks up the schema default (e.g. deductible=yes).
   *  - A shared URL with explicit filters (even "all" = empty string) is respected.
   *  - The user can always clear a default via the filter bar (clearing writes
   *    an empty sentinel param so reload distinguishes "cleared" from "never set").
   */
  function applySchemaDefaults(fields: ReportFilterField[], urlParams: URLSearchParams) {
    for (const field of fields) {
      if (field.defaultValue === undefined || field.defaultValue === null) continue;
      // Record that this field has a schema default so updateUrlState can write
      // an empty sentinel when the user clears it back to null.
      fieldsWithDefault.add(field.name);

      // Only apply default when the URL param is completely absent (has() returns false).
      // If the param exists with an empty value the user explicitly cleared it.
      if (urlParams.has(field.name)) continue;

      if (field.name === 'deductible') deductible = field.defaultValue;
      else if (field.name === 'year') year = field.defaultValue;
      else if (field.name === 'category') category = field.defaultValue;
      else if (field.name === 'person') person = field.defaultValue;
    }
  }

  onMount(async () => {
    parseUrlState();
    try {
      const types = await api.reports.getReportTypes();
      reportMeta = types.find((t) => t.reportType === reportType) || null;
      // Apply plugin default sort when the URL carries no explicit sort param.
      const urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.has('sortBy') && reportMeta?.defaultSortBy) {
        sortBy = reportMeta.defaultSortBy;
        sortOrder = reportMeta.defaultSortDescending ? 'desc' : 'asc';
      }
      // Apply schema field defaults for any filter not already set via URL params.
      if (reportMeta?.fields) {
        applySchemaDefaults(reportMeta.fields, urlParams);
      }
      if (!householdsStore.isDefaultHouseholdLoaded) {
        householdsStore.loadDefaultHousehold().catch(() => {});
      }
      await loadEntries();
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : 'Failed to load report';
    } finally {
      loading = false;
    }
    // Seed the location guard with the current URL so the first reactive run after
    // mounted=true is a no-op (onMount already did the initial load + defaults). See #239.
    prevLocationSearch = window.location.search;
    mounted = true;
  });

  async function loadEntries() {
    loading = true;
    error = '';
    try {
      const queryParams: Record<string, string> = {
        page: page.toString(),
        pageSize: pageSize.toString(),
        sortBy,
        sortOrder,
      };
      if (year) queryParams.year = year;
      if (category) queryParams.category = category;
      if (person) queryParams.person = person;
      if (deductible) queryParams.deductible = deductible;
      if (minAmount != null) queryParams.minAmount = minAmount.toString();
      if (maxAmount != null) queryParams.maxAmount = maxAmount.toString();
      // ADR-046: always pass the active household scope — "Global" is the default
      // household, not an "all households" sentinel. Omit only when no active
      // household is set (explicit cross-household query via empty filter).
      if ($activeHouseholdStore) {
        queryParams.householdId = $activeHouseholdStore;
      }

      response = await api.reports.queryReports(reportType, queryParams);
      entries = response.items;
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : 'Failed to load report entries';
    } finally {
      loading = false;
    }
  }

  function handleSort(e: CustomEvent<{ key: string; order: 'asc' | 'desc' | null }>) {
    sortBy = e.detail.order ? e.detail.key : 'entry_date';
    sortOrder = e.detail.order || 'desc';
    page = 0;
    updateUrlState();
    loadEntries();
  }

  function handleFilter(e: CustomEvent<{ key: string; value: FilterValue }>) {
    const { key, value } = e.detail;
    if (key === 'year') {
      year =
        Array.isArray(value) && value.length > 0
          ? value[0]
          : typeof value === 'string'
            ? value
            : null;
    } else if (key === 'category') {
      category =
        Array.isArray(value) && value.length > 0
          ? value[0]
          : typeof value === 'string'
            ? value
            : null;
    } else if (key === 'person') {
      person =
        Array.isArray(value) && value.length > 0
          ? value[0]
          : typeof value === 'string'
            ? value
            : null;
    } else if (key === 'deductible') {
      deductible =
        Array.isArray(value) && value.length > 0
          ? value[0]
          : typeof value === 'string'
            ? value
            : null;
    } else if (key === 'amount') {
      const v = value as { min?: number | null; max?: number | null } | null;
      minAmount = v?.min ?? null;
      maxAmount = v?.max ?? null;
    }
    page = 0;
    updateUrlState();
    loadEntries();
  }

  function handleClearAll() {
    year = null;
    category = null;
    person = null;
    deductible = null;
    minAmount = null;
    maxAmount = null;
    page = 0;
    updateUrlState();
    loadEntries();
  }

  function handlePageChange(e: CustomEvent<number>) {
    page = e.detail;
    updateUrlState();
    loadEntries();
  }

  function handlePageSizeChange(e: CustomEvent<number>) {
    pageSize = e.detail;
    page = 0;
    updateUrlState();
    loadEntries();
  }

  // Reload when active household changes
  $: {
    void $activeHouseholdStore;
    if (reportMeta) {
      page = 0;
      loadEntries();
    }
  }

  let exporting = false;

  async function handleExport() {
    exporting = true;
    try {
      const queryParams: Record<string, string> = {};
      if (year) queryParams.year = year;
      if (category) queryParams.category = category;
      if (person) queryParams.person = person;
      if (deductible) queryParams.deductible = deductible;
      if (minAmount != null) queryParams.minAmount = minAmount.toString();
      if (maxAmount != null) queryParams.maxAmount = maxAmount.toString();
      // ADR-046: same scope rule as loadEntries — always pass the active household.
      if ($activeHouseholdStore) {
        queryParams.householdId = $activeHouseholdStore;
      }

      const blob = await api.reports.exportReport(reportType, queryParams);
      const yearLabel = year || 'alle-jahre';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_${yearLabel}_export.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : $_('reports.export_error');
    } finally {
      exporting = false;
    }
  }
</script>

<PageHeader
  title={reportMeta?.displayName || reportType}
  breadcrumbs={[
    { label: $_('reports.title'), href: '/reports' },
    { label: reportMeta?.displayName || reportType },
  ]}
>
  <svelte:fragment slot="actions">
    {#if response}
      <span class="text-sm text-text-muted">
        {response.totalCount}
        {$_('reports.entries')}
      </span>
    {/if}
    <button
      data-testid="report-export-zip-button"
      class="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-border bg-surface hover:bg-surface-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      disabled={exporting || loading}
      on:click={handleExport}
      title={$_('reports.export_zip_tooltip')}
    >
      {#if exporting}
        <svg
          class="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          ></path>
        </svg>
        {$_('reports.export_loading')}
      {:else}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        {$_('reports.export_zip')}
      {/if}
    </button>
  </svelte:fragment>
</PageHeader>

{#if error}
  <div class="mb-4 px-3 py-2 bg-error/10 border border-error/30 rounded-lg text-sm text-error">
    {error}
  </div>
{/if}

{#if reportType === 'handwerkerleistungen-estg'}
  <HandwerkerReportTable
    {entries}
    {loading}
    {sortBy}
    {sortOrder}
    {filterState}
    filterSchema={reportMeta?.fields ?? []}
    dataSources={reportMeta?.dataSources ?? []}
    {page}
    {pageSize}
    totalCount={response?.totalCount ?? null}
    hasMore={response?.hasMore ?? false}
    on:sort={handleSort}
    on:filter={handleFilter}
    on:clearAll={handleClearAll}
    on:pageChange={handlePageChange}
    on:pageSizeChange={handlePageSizeChange}
  />
{:else}
  <ReportTable
    {entries}
    {loading}
    {sortBy}
    {sortOrder}
    {filterState}
    filterSchema={reportMeta?.fields ?? []}
    dataSources={reportMeta?.dataSources ?? []}
    {page}
    {pageSize}
    totalCount={response?.totalCount ?? null}
    hasMore={response?.hasMore ?? false}
    {members}
    on:sort={handleSort}
    on:filter={handleFilter}
    on:clearAll={handleClearAll}
    on:pageChange={handlePageChange}
    on:pageSizeChange={handlePageSizeChange}
  />
{/if}
